import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Calendar, User as UserIcon, TrendingUp, Info, ChevronRight, Sparkles, MessageCircle, RotateCcw, Award, Clock, Check, X, Scale, ShieldAlert } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { calcAposentadoria, formatBRL } from '../lib/calcAposentadoria';
import { firmsApi } from '../api/client';
import SEO from '../components/SEO';
import LawFirmCard from '../components/LawFirmCard';

const FAQS = [
  { q: 'Qual é a idade mínima para aposentadoria pelo INSS em 2025?', a: 'Após a Reforma da Previdência (EC 103/2019), a idade mínima urbana é 65 anos para homens e 62 anos para mulheres. Na área rural, é 60 anos para homens e 55 para mulheres.' },
  { q: 'Como funciona a regra de pontos?', a: 'É uma regra de transição para quem já contribuía antes da Reforma. Em 2025, mulheres precisam somar 92 pontos (idade + tempo de contribuição) e homens 102. A pontuação sobe 1 por ano até chegar em 100/105.' },
  { q: 'Como é calculado o valor do benefício?', a: 'A regra atual usa 60% da média de todas as contribuições desde julho de 1994, + 2% para cada ano acima do mínimo (15 anos para mulher, 20 para homem). O benefício não pode ser menor que 1 salário mínimo nem maior que o teto do INSS.' },
  { q: 'Posso me aposentar com menos de 15 anos de contribuição?', a: 'Não. Para trabalhadores urbanos, o mínimo é 15 anos (180 contribuições). Para quem começou a contribuir após a Reforma (13/11/2019), o mínimo passa a ser 20 anos para homens.' },
  { q: 'Vale a pena consultar um advogado previdenciário?', a: 'Sim. Um advogado especializado analisa seu CNIS, identifica períodos não computados, tempo especial (insalubridade), e pode aumentar significativamente o valor final do benefício ou reduzir o tempo de espera.' },
];

export default function RetirementCalculator() {
  const [form, setForm] = useState({
    gender: 'male',
    birthDate: '',
    startContribDate: '',
    monthlyContrib: '',
    type: 'idade',
  });
  const [showResult, setShowResult] = useState(false);
  const [prevFirms, setPrevFirms] = useState([]);

  const result = useMemo(() => (showResult ? calcAposentadoria(form) : null), [form, showResult]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (showResult) {
      firmsApi.list({ category: 'previdenciario', sort: 'rating', limit: 3 })
        .then(d => setPrevFirms(d.items))
        .catch(() => {});
    }
  }, [showResult]);

  const submit = (e) => {
    e.preventDefault();
    setShowResult(true);
    setTimeout(() => document.getElementById('resultado')?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const reset = () => {
    setShowResult(false);
    setForm({ gender: 'male', birthDate: '', startContribDate: '', monthlyContrib: '', type: 'idade' });
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(f => ({
      '@type': 'Question', name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div>
      <SEO
        title="Simulador de aposentadoria INSS grátis"
        description="Calcule sua aposentadoria no INSS: idade mínima, tempo de contribuição, regra de pontos e valor estimado do benefício. Baseado na Reforma da Previdência (EC 103/2019)."
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative bg-white border-b border-[#cbd8dc] text-[#122f36] overflow-hidden">
        <div className="aj-wrap max-w-[820px] py-14 md:py-16 relative">
          <div className="inline-flex items-center gap-2 bg-[#edf4f5] border border-[#cbd8dc] rounded-full px-4 py-1.5 text-xs font-medium text-[#075a6d] mb-4">
            <Calculator className="w-3.5 h-3.5" /> Simulador 100% gratuito
          </div>
          <h1 className="text-3xl md:text-5xl font-semibold leading-tight">Simulador de <span className="text-[#075a6d]">aposentadoria INSS</span></h1>
          <p className="mt-4 text-lg text-[#354c54] max-w-2xl leading-relaxed">Descubra quanto tempo falta para se aposentar, qual regra se aplica ao seu caso e o valor estimado do seu benefício. Baseado na Reforma da Previdência de 2019.</p>
        </div>
      </section>

      <div className="aj-wrap max-w-[820px] mt-8 relative pb-16">
        <form onSubmit={submit} className="bg-white rounded-[10px] border border-[#cbd8dc] p-6 md:p-8">
          <h2 className="text-lg font-semibold text-[#153e44] mb-5 flex items-center gap-2"><UserIcon className="w-5 h-5 text-[#075a6d]" /> Seus dados</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-2 block">Modalidade de aposentadoria</Label>
              <RadioGroup value={form.type} onValueChange={(v) => set('type', v)} className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {[
                  { v: 'idade', label: 'Por idade (urbano)', desc: 'H 65 / M 62 anos' },
                  { v: 'idade-rural', label: 'Rural', desc: 'H 60 / M 55 anos' },
                  { v: 'tempo', label: 'Por tempo (transição)', desc: 'Regra de pontos' },
                ].map(opt => (
                  <label key={opt.v} className={`p-3 rounded-[7px] border-2 cursor-pointer transition-all ${form.type === opt.v ? 'border-[#2f5d65] bg-[#edf4f5]' : 'border-[#cbd8dc] hover:border-[#bacbd0]'}`}>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value={opt.v} id={`t-${opt.v}`} />
                      <span className="text-sm font-medium text-[#243b42]">{opt.label}</span>
                    </div>
                    <div className="text-xs text-[#52676e] mt-1 ml-6">{opt.desc}</div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-2 block">Sexo</Label>
              <RadioGroup value={form.gender} onValueChange={(v) => set('gender', v)} className="grid grid-cols-2 gap-2">
                {[{ v: 'male', label: 'Masculino' }, { v: 'female', label: 'Feminino' }].map(opt => (
                  <label key={opt.v} className={`flex items-center gap-2 p-3 rounded-[7px] border-2 cursor-pointer transition-all ${form.gender === opt.v ? 'border-[#2f5d65] bg-[#edf4f5]' : 'border-[#cbd8dc] hover:border-[#bacbd0]'}`}>
                    <RadioGroupItem value={opt.v} id={`g-${opt.v}`} />
                    <span className="text-sm text-[#243b42]">{opt.label}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider">Contribuição média mensal (R$)</Label>
              <Input type="number" required min="1" step="0.01" value={form.monthlyContrib} onChange={(e) => set('monthlyContrib', e.target.value)} placeholder="Ex: 3200" className="mt-1.5 h-11" />
              <p className="text-xs text-[#52676e] mt-1">Média das suas contribuições desde julho/1994.</p>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider">Data de nascimento</Label>
              <div className="relative mt-1.5">
                <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" />
                <Input type="date" required value={form.birthDate} onChange={(e) => set('birthDate', e.target.value)} className="pl-9 h-11" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider">Início das contribuições</Label>
              <div className="relative mt-1.5">
                <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" />
                <Input type="date" required value={form.startContribDate} onChange={(e) => set('startContribDate', e.target.value)} className="pl-9 h-11" />
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-3 mt-6 pt-6 border-t border-[#e1e9eb]">
            <Button type="submit" className="flex-1 h-12 bg-[#153e44] hover:bg-[#0c3036] text-white font-semibold text-base"><Calculator className="w-5 h-5 mr-2" /> Simular aposentadoria</Button>
            <Button type="button" variant="outline" onClick={reset} className="h-12"><RotateCcw className="w-4 h-4 mr-2" /> Limpar</Button>
          </div>
        </form>

        {result && !result.error && (
          <div id="resultado" className="mt-8 space-y-5">
            <div className={`rounded-[10px] p-6 md:p-8 border border-[#cbd8dc] border-l-4 ${result.qualifies ? 'border-l-emerald-700' : 'border-l-[#153e44]'} bg-[#f7fafb] text-[#243b42]`}>
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-[7px] shrink-0 flex items-center justify-center ${result.qualifies ? 'bg-emerald-100' : 'bg-[#edf4f5]'}`}>
                  {result.qualifies ? <Check className="w-8 h-8 text-emerald-700" /> : <Clock className="w-8 h-8 text-[#153e44]" />}
                </div>
                <div className="flex-1">
                  <div className="text-xs font-semibold uppercase tracking-widest mb-1 text-[#425a62]">{result.qualifies ? 'Você já pode se aposentar!' : 'Ainda não é elegível'}</div>
                  <h3 className="text-2xl md:text-3xl font-semibold">{result.label}</h3>
                  {result.qualifies ? (
                    <p className="mt-2 text-[#354c54]">Você atende aos requisitos para dar entrada no benefício. Consulte um advogado previdenciário para dar entrada ao pedido.</p>
                  ) : (
                    <p className="mt-2 text-[#354c54]">
                      Falta{result.missingAge > 0 ? ` ${Math.ceil(result.missingAge)} anos de idade` : ''}
                      {result.missingContrib > 0 ? ` ${result.missingAge > 0 ? 'e' : ''} ${result.missingContrib} anos de contribuição` : ''}
                      {result.missingPoints > 0 ? ` (faltam ${result.missingPoints} pontos)` : ''}.
                      {result.readyDate && ` Estimativa: ${result.readyDate.getFullYear()}.`}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-[#6b858e]">
                <div className="text-xs uppercase tracking-widest text-[#425a62] mb-1">Valor estimado do benefício</div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <div className="text-4xl md:text-5xl font-semibold text-[#122f36]">{formatBRL(result.benefit)}</div>
                  <div className="text-sm text-[#425a62]">/mês ({result.percent}% da média)</div>
                </div>
                <div className="text-xs opacity-75 mt-2">Limitado ao teto do INSS ({formatBRL(result.ceiling)}) e piso do salário mínimo ({formatBRL(result.minBenefit)}).</div>
              </div>
            </div>

            {/* Progress cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <ProgressCard
                title="Idade"
                current={result.ageYears}
                required={result.requiredAge}
                unit="anos"
                icon={UserIcon}
              />
              <ProgressCard
                title="Tempo de contribuição"
                current={result.contribYears}
                required={result.requiredContrib}
                unit="anos"
                icon={Clock}
              />
              {result.type === 'tempo' && (
                <ProgressCard
                  title="Pontos (idade + tempo)"
                  current={result.currentPoints}
                  required={result.pointsGoal}
                  unit="pts"
                  icon={Award}
                />
              )}
              {result.type !== 'tempo' && (
                <div className="bg-white border border-[#cbd8dc] rounded-[7px] p-5">
                  <div className="text-xs text-[#52676e] uppercase tracking-wider mb-2">Regra do cálculo</div>
                  <div className="font-semibold text-[#153e44]">60% + 2% por ano adicional</div>
                  <p className="text-sm text-[#425a62] mt-1">Você tem direito a {result.percent}% da média das suas contribuições.</p>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-amber-50 border border-amber-200 rounded-[10px] p-5">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-amber-900 mb-2">Importante</div>
                  <ul className="space-y-1.5 text-sm text-amber-900">
                    <li className="flex gap-2"><span>•</span><span>Este é um cálculo simplificado. Valores reais dependem do seu CNIS, tempo especial e regras de transição específicas.</span></li>
                    <li className="flex gap-2"><span>•</span><span>Um advogado previdenciário pode identificar períodos de tempo especial, tempo rural e contribuições não computadas — aumentando o benefício em até 40%.</span></li>
                    <li className="flex gap-2"><span>•</span><span>Sempre solicite o cálculo oficial do INSS pelo Meu INSS antes de dar entrada.</span></li>
                  </ul>
                </div>
              </div>
            </div>

            {/* CTA lawyers */}
            <div className="bg-white rounded-[10px] border-2 border-[#cbd8dc] p-6 md:p-8">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-[#edf4f5] border border-[#cbd8dc] rounded-full px-3 py-1 text-xs font-medium text-[#153e44] mb-3">
                  <ShieldAlert className="w-3.5 h-3.5" /> Maximize seu benefício
                </div>
                <h3 className="text-xl md:text-2xl font-semibold text-[#153e44]">Fale com um advogado previdenciário</h3>
                <p className="text-[#425a62] mt-2 max-w-2xl mx-auto">Ele analisa seu CNIS, identifica tempo especial e revisa contribuições. Muitas aposentadorias podem ser aumentadas em 20% ou mais.</p>
              </div>
              {prevFirms.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                  {prevFirms.map(f => <LawFirmCard key={f.id} firm={f} />)}
                </div>
              )}
              <div className="flex flex-wrap justify-center gap-3 mt-6">
                <Link to="/match"><Button className="bg-[#153e44] hover:bg-[#0c3036] h-11"><Sparkles className="w-4 h-4 mr-2" /> Match com IA</Button></Link>
                <Link to="/categoria/previdenciario"><Button variant="outline" className="h-11">Ver previdenciários <ChevronRight className="w-4 h-4 ml-1" /></Button></Link>
              </div>
            </div>
          </div>
        )}

        {result?.error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-[7px] p-5 text-red-700 text-sm">{result.error}</div>
        )}

        {!result && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: Clock, title: 'Tempo mínimo', desc: 'Descubra quantos anos faltam para você atingir os requisitos.' },
              { icon: Award, title: 'Regra de pontos', desc: 'Simulação automática da regra de transição por pontos (86/96).' },
              { icon: TrendingUp, title: 'Valor estimado', desc: 'Cálculo do benefício com base na regra de 60% + 2% pós-reforma.' },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-[#cbd8dc] rounded-[7px] p-5">
                <div className="w-10 h-10 rounded-[7px] bg-[#153e44] flex items-center justify-center mb-3"><s.icon className="w-5 h-5 text-[#075a6d]" /></div>
                <div className="font-semibold text-[#153e44]">{s.title}</div>
                <p className="text-sm text-[#425a62] mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <section className="bg-[#f2f6f7] py-14">
        <div className="aj-wrap max-w-[820px]">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#153e44] mb-6">Perguntas frequentes sobre aposentadoria</h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <details key={i} className="group bg-white rounded-[7px] border border-[#cbd8dc] p-5 open:shadow-md">
                <summary className="font-semibold text-[#153e44] cursor-pointer list-none flex items-center justify-between gap-4">
                  {f.q}
                  <ChevronRight className="w-5 h-5 text-[#075a6d] transition-transform group-open:rotate-90 shrink-0" />
                </summary>
                <p className="mt-3 text-[#354c54] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function ProgressCard({ title, current, required, unit, icon: Icon }) {
  const pct = Math.min(100, (current / required) * 100);
  const done = current >= required;
  return (
    <div className="bg-white border border-[#cbd8dc] rounded-[7px] p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#075a6d]" />
          <div className="text-xs text-[#52676e] uppercase tracking-wider">{title}</div>
        </div>
        {done ? <Check className="w-4 h-4 text-emerald-600" /> : <X className="w-4 h-4 text-[#6b858e]" />}
      </div>
      <div className="text-2xl font-semibold text-[#153e44]">{current} <span className="text-sm text-[#6b858e] font-normal">/ {required} {unit}</span></div>
      <div className="w-full bg-[#f2f6f7] rounded-full h-2 mt-3 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${done ? 'bg-emerald-500' : 'bg-[#153e44]'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
